import React, { useState, useMemo, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation } from '@apollo/client';
import {
  ErrorMessage,
  PageTitle,
  Button,
  useModal,
  connectModal,
  Link,
  NotFound,
  SelectDropdown,
  Alert,
} from '@gsa/afp-component-library';
import { useAppAbility } from '@gsa/afp-shared-ui-utils';
import pLimit from 'p-limit';
import {
  OPERATIONS,
  SUBJECTS,
  emDashUnicode,
} from '../../../utilities/constants';
import { GET_CONTRACT_HEADER_BY_ID } from '../contract-header/contract-header.gql';
import ContractDetails from '../components/contract-details';
import TableWrapper from '../components/table-wrapper';
import ToastMessage from '../../../components/Toast/toast';
import { ContractLineListingActions } from './contract-line-listing-actions';
import UpdateLineItemsModal from './update-contract-line-modal';
import DeleteContractLineModal from './delete-items-modal';
import ReviewItemsModal from './review-items-modal';
import CarryoverLinesModal from './carryover-lines-modal/carryover-lines-modal';
import { getColumns, RowSubComponent, ITEM_PER_PAGE_OPTIONS } from './columns';
import {
  GET_CONTRACT_LINES_BY_CRITERIA,
  GET_CARRYOVER_CONTRACT_LINES,
  GET_CARRYOVER_CONTRACT_LINE_IDS,
  DELETE_CONTRACT_LINE_BY_ID,
  UPDATE_CONTRACT_LINES,
  GET_CONTRACT_LINES_FOR_FILTERS,
} from './lines-query';
import ContractLineListingFilter, {
  DEFAULT_FILTERS,
} from './contract-line-listing-search';
import OverlaySpinner from '../../../components/overlay-spinner';
import { isContractSOP } from '../components/contract-helpers';
import { ContractLineListingBreadcrumbs } from './contract-line-listing-breadcrumbs';
import './contract-line-listing.scss';

const ContractLineListing = () => {
  const { contractHeaderId } = useParams();
  const navigate = useNavigate();

  const [alert, setAlert] = useState(null);
  const [order, setOrder] = useState([['scheduleLine', 'ASC']]);
  const [criteria, setCriteria] = useState({ contractHeaderId });
  const [paginationState, setPaginationState] = useState({
    limit: ITEM_PER_PAGE_OPTIONS[0],
    offset: 0,
    currentPage: 1,
  });
  const [selectedItems, setSelectedItems] = useState([]);
  const [updateItems, setUpdateItems] = useState([]);
  const [updatedData, setUpdatedData] = useState({});
  const [updatingMessage, setUpdatingMessage] = useState(null);

  const { limit, offset, currentPage } = paginationState;

  const [manualLoading, setManualLoading] = useState(false);
  const [deletingMessage, setDeletingMessage] = useState(null);

  const {
    loading: loadingLines,
    error: errorLines,
    data: dataLines,
    refetch: refetchContractLines,
  } = useQuery(GET_CONTRACT_LINES_BY_CRITERIA, {
    fetchPolicy: 'network-only',
    skip: !criteria,
    variables: {
      criteria,
      limit,
      offset,
      order,
    },
  });

  const {
    data: dataLinesForFilters,
    loading: loadingLinesForFilters,
    refetch: refetchLinesForFilters,
  } = useQuery(GET_CONTRACT_LINES_FOR_FILTERS, {
    fetchPolicy: 'network-only',
    skip: !contractHeaderId,
    variables: { contractHeaderId },
  });

  const refetchLines = (newOffset) => {
    if (newOffset == null) refetchContractLines();
    else {
      setPaginationState((prev) => ({
        limit: prev.limit,
        offset: newOffset,
        currentPage: newOffset / prev.limit + 1,
      }));
      refetchContractLines({
        variables: {
          criteria,
          limit,
          offset: newOffset,
          order,
        },
      });
    }
    refetchLinesForFilters();
  };

  const {
    data: dataHeader,
    error: errorHeader,
    refetch: refetchHeader,
    called,
    loading: loadingHeader,
  } = useQuery(GET_CONTRACT_HEADER_BY_ID, {
    variables: { contractHeaderId },
  });

  const [deleteContractLine, { loading: loadingDeleteLines }] = useMutation(
    DELETE_CONTRACT_LINE_BY_ID,
  );

  const [updateContractLines, { loading: updating }] = useMutation(
    UPDATE_CONTRACT_LINES,
  );

  const contractLinesForFilter =
    dataLinesForFilters?.getContractLinesForFilters ?? [];
  const contractLineCount = dataLines?.getContractLinesByCriteria?.count || 0;
  const contract = dataHeader?.getContractHeaderById || {};

  const vendor = contract.vendor || {};
  const vendorId = vendor.id || '';

  const isSop = isContractSOP(contract.solicitation?.purchaseTypeCode);
  const programs = contract.solicitation?.programs || [];

  const contractHeaderRenewals = contract.contractHeaderRenewals ?? [];
  const editable = contractHeaderRenewals.every(
    ({ renewalYear }) => contract.renewalYear >= renewalYear,
  );
  const isCurrentYear = editable && contractHeaderRenewals.length > 0;
  const isPreviousYear =
    contractHeaderRenewals.length > 1 &&
    contract.renewalYear === contractHeaderRenewals[1].renewalYear;
  const toGetCarryover =
    contract.fleetAwarded && isSop && (isCurrentYear || isPreviousYear);

  const yearOptions = contractHeaderRenewals.map((chr) => ({
    value: chr.contractHeaderId,
    label: `Contract year ${chr.renewalYear ?? chr.contractYear}`,
  }));
  const onYearChange = ({ target: { value } }) => {
    navigate(`/catalog/contract/contract-header/${value}/lines`);
  };
  const versionOptions = [...(contract.contractHeaderVersions ?? [])]
    .sort((a, b) => (a.versionNumber < b.versionNumber ? 1 : -1))
    .map((chv, i) => ({
      value: i === 0 ? '' : chv.versionNumber,
      label: `version ${chv.versionNumber}`,
    }));
  const onVersionChange = ({ target: { value } }) => {
    if (value)
      navigate(
        `/catalog/contract/contract-header/${contractHeaderId}/history/${value}/lines`,
      );
  };

  const [carryoverLines, setCarryoverLines] = useState([]);
  const [activeCarryoverLines, setActiveCarryoverLines] = useState([]);
  const { data: carryoverLinesData, refetch: refetchCarryoverLines } = useQuery(
    isCurrentYear
      ? GET_CARRYOVER_CONTRACT_LINES
      : GET_CARRYOVER_CONTRACT_LINE_IDS,
    {
      fetchPolicy: 'network-only',
      skip: !toGetCarryover || contractHeaderRenewals.length < 2,
      variables: {
        contractHeaderId: contractHeaderRenewals[0]?.contractHeaderId,
      },
    },
  );
  useEffect(() => {
    if (carryoverLinesData?.getCarryoverContractLines) {
      const lines = carryoverLinesData.getCarryoverContractLines;
      setCarryoverLines(lines);
      setActiveCarryoverLines(
        lines.filter(
          (line) =>
            line.orderEndDate && new Date(line.orderEndDate) > new Date(),
        ),
      );
    }
  }, [carryoverLinesData]);

  const [contractLinesData, setContractLinesData] = useState([]);

  const [filters, setFilters] = useState(DEFAULT_FILTERS);

  useEffect(() => {
    if (!dataLines?.getContractLinesByCriteria) return;
    const fetchedLines = dataLines.getContractLinesByCriteria.rows.map(
      (line) => {
        return {
          ...line,
          isActive:
            line.orderEndDate && new Date(line.orderEndDate) >= new Date(),
          isCarryover: carryoverLines.some((cl) => cl.id === line.id),
        };
      },
    );

    if (!programs.length) {
      setContractLinesData(fetchedLines);
      return;
    }

    const programNamesWithIds = programs?.reduce((a, c) => {
      return {
        ...a,
        [c.program]: {
          stdIds: c.solicitationLines.map((item) =>
            String(item.standardItem.standardItemNumber),
          ),
          solLines: c.solicitationLines.map((item) => String(item.id)),
        },
      };
    }, {});

    const getProgramName = (programNames, { stdId, solId }) => {
      let result;
      if (stdId && solId) {
        result = Object.entries(programNames)
          ?.map(([key, value]) => {
            if (
              value.stdIds?.includes(stdId) &&
              value.solLines?.includes(solId)
            ) {
              return key;
            }
            return null;
          })
          .filter((item) => item)[0];
        return result;
      }
      if (solId) {
        result = Object.entries(programNames)
          ?.map(([key, value]) => {
            if (value.solLines?.includes(solId)) {
              return key;
            }
            return null;
          })
          .filter((item) => item)[0];
        return result;
      }
      if (stdId) {
        result = Object.entries(programNames)
          ?.map(([key, value]) => {
            if (value.stdIds?.includes(stdId)) {
              return key;
            }
            return null;
          })
          .filter((item) => item)[0];
        return result;
      }

      return result;
    };

    setContractLinesData(
      fetchedLines.map((line) => {
        return {
          ...line,
          programName: getProgramName(programNamesWithIds, {
            // stdId: line.standardItem.standardItemNumber,
            stdId: null,
            solId: String(line.solicitationLineId),
          }),
        };
      }),
    );
  }, [dataLines, programs, carryoverLines]);

  const {
    isOpen: updateContractLineIsOpen,
    openModal: updateContractLineOpenModal,
    closeModal: updateContractLineCloseModal,
  } = useModal();
  const {
    isOpen: reviewItemsIsOpen,
    openModal: reviewItemsOpenModal,
    closeModal: reviewItemsCloseModal,
  } = useModal();
  const {
    isOpen: deleteContractLineIsOpen,
    openModal: deleteContractLineOpenModal,
    closeModal: deleteContractLineCloseModal,
  } = useModal();
  const {
    isOpen: carryoverIsOpen,
    openModal: carryoverOpenModal,
    closeModal: carryoverCloseModal,
  } = useModal();

  const ability = useAppAbility();
  const canUpdateContract =
    editable && ability.can(OPERATIONS.Update, SUBJECTS.Contract);

  const ConnectedUpdateLineItemModal = connectModal(UpdateLineItemsModal);
  const ConnectedReviewItemsModal = connectModal(ReviewItemsModal);
  const ConnectedDeleteContractLineModal = connectModal(
    DeleteContractLineModal,
  );

  const onApplyFilters = (isReset) => {
    const newCriteria = { contractHeaderId };
    if (isReset) {
      setCriteria(newCriteria);
      return;
    }
    // eslint-disable-next-line
    for (const key of Object.keys(filters)) {
      if (filters[key]) {
        if (['makeCode', 'chassisMakeCode', 'modelYear'].includes(key))
          newCriteria[key] = +filters[key];
        else newCriteria[key] = filters[key];
      }
    }
    setCriteria(newCriteria);
  };

  const handleRowSelect = ({ selectedFlatRows }) => {
    const rows = selectedFlatRows.map((row) => row.original);
    setSelectedItems(rows);
  };

  const handleDelete = async () => {
    deleteContractLineCloseModal();

    const successItems = [];
    const associatedOrdersItems = [];
    const errorItems = [];

    const promiseLimit = pLimit(3);

    setManualLoading(true);

    const promises = selectedItems.map((item, index) => {
      return promiseLimit(async () => {
        try {
          setDeletingMessage(
            <div className="text-center">
              Deleting line {index + 1} of {selectedItems.length}
            </div>,
          );
          await deleteContractLine({
            variables: { contractLineId: parseInt(item.id, 10) },
          });
          successItems.push(item);
        } catch (err) {
          errorItems.push({
            scheduleLine: item.scheduleLine,
            errorMessage: err.message,
          });
        }
      });
    });

    try {
      await Promise.all(promises);
    } finally {
      setManualLoading(false);
      setDeletingMessage(null);
      if (toGetCarryover && contractHeaderRenewals.length > 1)
        refetchCarryoverLines();
    }

    if (successItems.length + errorItems.length > 0) {
      let alertType = 'warning';
      if (!successItems.length) alertType = 'error';
      else if (!errorItems.length) alertType = 'success';

      const successLines = successItems
        .map((item) => item.scheduleLine)
        .join(', ');
      const message = (
        <>
          {successItems.length > 0 && (
            <div className="margin-bottom-1">
              The contract line(s) <b>{successLines}</b> have been deleted.
            </div>
          )}
          {errorItems.length > 0 && (
            <>
              <div>Unable to delete the following contract line(s):</div>
              {errorItems.map((item) => (
                <li key={item.scheduleLine} className="margin-left-4">
                  <b>{item.scheduleLine}</b>: {item.errorMessage}
                </li>
              ))}
            </>
          )}
        </>
      );
      setAlert({ type: alertType, message });
    }

    if (successItems.length > 0) {
      const newLineCount = contractLineCount - successItems.length;
      if (newLineCount > 0 && newLineCount <= paginationState.offset) {
        const newOffset =
          (Math.ceil(newLineCount / paginationState.limit) - 1) *
          paginationState.limit;
        refetchLines(newOffset);
      } else refetchLines();
    }
  };

  const handleUpdate = async (lineItems, updates, formData) => {
    const errors = [];
    const updatedContractLines = [];
    for (let i = 0; i < lineItems.length; i += 1) {
      const { id, scheduleLine } = lineItems[i];
      const input = {
        contractLineIds: [parseInt(id, 10)],
        ...updates,
      };
      setUpdatingMessage(
        <div className="text-center">
          Updating contract line {i + 1} of {lineItems.length} ... <br />
          {scheduleLine}
        </div>,
      );
      try {
        // eslint-disable-next-line
        const res = await updateContractLines({ variables: { input } });
        const data = res.data.updateMultipleContractLines;
        updatedContractLines.push(...data.updatedContractLines);
        errors.push(...data.errors.map((e) => ({ ...e, scheduleLine })));
      } catch (err) {
        errors.push({ scheduleLine, errorMessage: err.message });
      }
    }

    setUpdatedData({
      formData,
      lineItems,
      errors,
      updatedContractLines,
    });

    setAlert({
      type: updatedContractLines.length ? 'success' : 'error',
      message: (
        <div>
          <span className="body-bold">
            {updatedContractLines.length > 0
              ? `${updatedContractLines.length} contract lines have been
          updated successfully`
              : ''}
            {updatedContractLines.length > 0 && errors.length > 0
              ? ' and '
              : ' '}
            {errors.length > 0
              ? `${errors.length} contract lines could not be updated`
              : ''}
            {'. '}
          </span>
          <Button
            onClick={reviewItemsOpenModal}
            variant="unstyled"
            label="Review updates"
            style={{ padding: 0 }}
          />
        </div>
      ),
    });
    refetchLines();
  };

  const columns = useMemo(() => getColumns({ isSop }), [isSop]);

  if (!called) {
    return null;
  }

  if (errorLines) {
    return (
      <div id="contract-line-listing">
        <ContractLineListingBreadcrumbs />
        {errorLines && (
          <ErrorMessage>An error occurred: {errorLines.message}</ErrorMessage>
        )}
      </div>
    );
  }

  if (called && !loadingHeader && !dataHeader?.getContractHeaderById) {
    return (
      <div id="contract-line-listing">
        <ContractLineListingBreadcrumbs />
        <NotFound />
      </div>
    );
  }

  return (
    <div id="contract-line-listing">
      {/* <StandardItemBodyChassisPanel hasChassis /> */}
      <ContractLineListingBreadcrumbs />
      {(loadingHeader ||
        loadingLines ||
        loadingLinesForFilters ||
        loadingDeleteLines ||
        manualLoading) && <OverlaySpinner message={deletingMessage} />}
      {updating && <OverlaySpinner message={updatingMessage} />}
      {errorLines && (
        <ErrorMessage>An error occurred: {errorLines.message}</ErrorMessage>
      )}
      {alert && (
        <ToastMessage
          type={alert.type}
          message={alert.message}
          onClose={() => setAlert(null)}
          closable
          className="margin-bottom-2"
        />
      )}
      {!loadingHeader &&
        !errorHeader &&
        contract &&
        !errorLines &&
        dataLines && (
          <>
            <div className="grid-row grid-gap">
              <div className="grid-col-8">
                <PageTitle
                  title={
                    <>
                      Contract uPIID:{' '}
                      {contract.contractUpiid ||
                        contract.solicitation?.solicitationNumber ||
                        emDashUnicode}
                    </>
                  }
                />
              </div>
              <div className="grid-col-4 display-flex flex-justify-end">
                <SelectDropdown
                  id="contract_year_sel"
                  aria-label="contract year"
                  onChange={onYearChange}
                  options={yearOptions}
                  value={contractHeaderId}
                  className="text-bold font-body-md"
                  style={{ width: '13rem' }}
                />
                <span className="padding-left-1" />
                <SelectDropdown
                  id="contract_version_sel"
                  aria-label="contract version"
                  onChange={onVersionChange}
                  options={versionOptions}
                  value={''}
                  className="text-bold font-body-md"
                  style={{ width: '8.5rem' }}
                />
              </div>
            </div>
            <div className="grid-row grid-gap margin-bottom-2">
              <div className="grid-col-10 margin-top-1">
                <span>Contractor: </span>
                <Link
                  href={`${window.AFP_CONFIG.appURLs.home}/vendor/details/${vendorId}`}
                >
                  {vendor.vendorName}
                </Link>
              </div>
              <div className="grid-col-2 display-flex flex-align-end flex-justify-end">
                <ContractLineListingActions
                  contractHeader={contract}
                  setAlert={setAlert}
                  refetchLines={refetchLines}
                  refetchHeader={refetchHeader}
                  refetchCarryoverLines={
                    !toGetCarryover || contractHeaderRenewals.length < 2
                      ? () => null
                      : refetchCarryoverLines
                  }
                  editable={editable}
                />
              </div>
            </div>
            <ContractDetails contract={contract} />
            {isCurrentYear && activeCarryoverLines.length > 0 && (
              <Alert type="warning">
                There are {activeCarryoverLines.length} contract line(s) carried
                over from contract year {contract.renewalYear - 1}.
                <Button
                  variant="unstyled"
                  onClick={carryoverOpenModal}
                  className="padding-left-1"
                  label="View lines"
                />
              </Alert>
            )}
            <ContractLineListingFilter
              contractLines={contractLinesForFilter}
              filters={filters}
              setFilters={setFilters}
              onApplyFilters={onApplyFilters}
              isSop={!!isSop}
            />
            <div className="grid-row grid-gap margin-top-2 margin-bottom-2">
              {canUpdateContract && (
                <div className="grid-col-12">
                  {isSop && (
                    <Button
                      label="Update selected lines"
                      variant="primary"
                      onClick={() => {
                        setUpdateItems(selectedItems);
                        updateContractLineOpenModal();
                      }}
                      disabled={selectedItems.length === 0}
                    />
                  )}
                  <Button
                    label="Delete selected lines"
                    variant="danger"
                    onClick={deleteContractLineOpenModal}
                    disabled={selectedItems.length === 0}
                    className="margin-left-2"
                  />
                </div>
              )}
            </div>
            <TableWrapper
              tableProps={{
                data: contractLinesData,
                columns,
                // defaultSort: 'scheduleLine asc',
                onSort: setOrder,
                defaultSort: order,
                expandable: true,
                selectable: canUpdateContract,
                onRowSelect: handleRowSelect,
                renderRowSubComponent: (props) => (
                  <RowSubComponent {...props} isSop={isSop} />
                ),
              }}
              paginationProps={{
                itemsPerPageOptions: ITEM_PER_PAGE_OPTIONS,
                onPageChange: (newPage, itemsPerPage) => {
                  setPaginationState({
                    ...paginationState,
                    currentPage: newPage,
                    offset: (newPage - 1) * itemsPerPage,
                    limit: itemsPerPage,
                  });
                },
                variant: 'advanced',
                itemsPerPage: limit,
                currentPage,
                itemsCount: contractLineCount,
                isReset: false,
              }}
              emptyStateText="There are no contract lines associated with this contract."
            />

            <ConnectedUpdateLineItemModal
              isOpen={updateContractLineIsOpen}
              closeModal={updateContractLineCloseModal}
              lineItems={updateItems}
              contractHeaderId={contractHeaderId}
              headerData={contract}
              onUpdate={handleUpdate}
            />

            <ConnectedReviewItemsModal
              isOpen={reviewItemsIsOpen}
              closeModal={reviewItemsCloseModal}
              items={updatedData?.items}
              data={updatedData?.formData}
              errors={updatedData?.errors}
              updatedContractLines={updatedData?.updatedContractLines}
            />
            <ConnectedDeleteContractLineModal
              isOpen={deleteContractLineIsOpen}
              closeModal={deleteContractLineCloseModal}
              onDelete={handleDelete}
              selectedItems={selectedItems}
            />
            <CarryoverLinesModal
              isOpen={carryoverIsOpen}
              onClose={carryoverCloseModal}
              contractHeader={contractHeaderRenewals[1]}
              lines={carryoverLines}
            />
          </>
        )}
    </div>
  );
};

export default ContractLineListing;
